import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { agentService } from '../services/agentService';
import { productUrl } from '../utils/seo';
import './InlineAgent.css';

function InlineAgent({ product, onClose }) {
  const [commentary, setCommentary] = useState('');
  const [isStreaming, setIsStreaming] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatStreaming, setChatStreaming] = useState(false);
  const [error, setError] = useState(null);

  const commentaryRef = useRef('');
  const eventSourceRef = useRef(null);
  const containerRef = useRef(null);
  const chatHistoryRef = useRef(null);

  const closeEventSource = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  // SSE for initial product commentary
  useEffect(() => {
    commentaryRef.current = '';
    setCommentary('');
    setIsStreaming(true);
    setError(null);
    setChatMessages([]);

    const es = agentService.getProductAgent(product.id);
    eventSourceRef.current = es;

    es.addEventListener('agent_chunk', (e) => {
      try {
        const data = JSON.parse(e.data);
        commentaryRef.current += data.text;
        setCommentary(commentaryRef.current);
      } catch {}
    });

    es.addEventListener('agent_done', () => {
      setIsStreaming(false);
      closeEventSource();
    });

    es.onerror = () => {
      setError('Error al conectar con el agente');
      setIsStreaming(false);
      closeEventSource();
    };

    // Auto-scroll into view
    setTimeout(() => {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);

    return () => closeEventSource();
  }, [product.id, closeEventSource]);

  // Auto-scroll chat history when new messages arrive
  useEffect(() => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const handleChatSend = (e) => {
    e.preventDefault();
    const msg = chatInput.trim();
    if (!msg || chatStreaming) return;

    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', text: msg }]);
    setChatStreaming(true);

    let agentReply = '';
    const es = agentService.sendChat(product.id, msg);
    eventSourceRef.current = es;

    es.addEventListener('agent_chunk', (ev) => {
      try {
        const data = JSON.parse(ev.data);
        agentReply += data.text;
        setChatMessages(prev => {
          const copy = [...prev];
          const lastIdx = copy.length - 1;
          if (lastIdx >= 0 && copy[lastIdx].role === 'agent' && copy[lastIdx].streaming) {
            copy[lastIdx] = { role: 'agent', text: agentReply, streaming: true };
          } else {
            copy.push({ role: 'agent', text: agentReply, streaming: true });
          }
          return copy;
        });
      } catch {}
    });

    es.addEventListener('agent_done', () => {
      setChatStreaming(false);
      setChatMessages(prev => {
        const copy = [...prev];
        const lastIdx = copy.length - 1;
        if (lastIdx >= 0 && copy[lastIdx].role === 'agent') {
          copy[lastIdx] = { ...copy[lastIdx], streaming: false };
        }
        return copy;
      });
      closeEventSource();
    });

    es.onerror = () => {
      setChatStreaming(false);
      closeEventSource();
    };
  };

  const priceDisplay = product.priceOnConsultation
    ? 'Precio a consultar'
    : `${product.currency === 'USD' ? 'US$' : '$'}${Number(product.price).toLocaleString('es-UY')}`;

  return (
    <div className="inline-agent" ref={containerRef}>
      {/* Header */}
      <div className="inline-agent-header">
        <div className="inline-agent-label">
          <span>🤖</span> Agente IA
        </div>
        <button className="inline-agent-close" onClick={onClose}>
          ✕ Cerrar
        </button>
      </div>

      {/* Body */}
      <div className="inline-agent-body">
        {/* Left: Product info */}
        <div className="inline-agent-product">
          {product.imageUrls?.length > 0 ? (
            <div className="inline-agent-thumb">
              <img src={product.imageUrls[0]} alt={product.title} />
            </div>
          ) : (
            <div className="inline-agent-thumb-placeholder">🛒</div>
          )}
          <h4 className="inline-agent-product-title">{product.title}</h4>
          <div className="inline-agent-product-price">{priceDisplay}</div>
          <div className="inline-agent-product-seller">
            {product.sellerUsername}
            {product.department && ` · ${product.department}`}
          </div>
          <Link to={productUrl(product)} className="inline-agent-detail-link">
            → Ver detalle completo
          </Link>
        </div>

        {/* Right: Agent content */}
        <div className="inline-agent-content">
          {/* Commentary */}
          <div className="inline-agent-commentary">
            <div className="inline-agent-avatar">🤖</div>
            <div className="inline-agent-bubble">
              {commentary || (!isStreaming ? 'Sin comentarios.' : '')}
              {isStreaming && <span className="inline-agent-typing">▊</span>}
              {!commentary && isStreaming && (
                <span className="inline-agent-thinking">Analizando publicación...</span>
              )}
            </div>
          </div>

          {error && <div className="inline-agent-error">{error}</div>}

          {/* Chat history */}
          {chatMessages.length > 0 && (
            <div className="inline-agent-chat-history" ref={chatHistoryRef}>
              {chatMessages.map((m, i) => (
                <div key={i} className={`inline-agent-chat-msg inline-agent-chat-msg--${m.role}`}>
                  {m.role === 'agent' && <div className="inline-agent-avatar">🤖</div>}
                  <div className="inline-agent-bubble">
                    {m.text}
                    {m.streaming && <span className="inline-agent-typing">▊</span>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Chat input */}
          <form className="inline-agent-chat" onSubmit={handleChatSend}>
            <input
              type="text"
              className="inline-agent-chat-input"
              placeholder="Preguntale algo sobre este producto..."
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              disabled={isStreaming || chatStreaming}
            />
            <button
              type="submit"
              className="inline-agent-send"
              disabled={isStreaming || chatStreaming || !chatInput.trim()}
            >
              Enviar
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default InlineAgent;
